<template>
  <div class="form">
    <div class="form__content">
      <h1 class="extra-large-headline">Identity Confirmation</h1>
      <p class="bodycopy">
        We've noticed that this is your first time buying crypto-currency with
        us. You will need to complete a quick one-off identity verification.
      </p>
    </div>
    <SkeleLoader
      v-if="isLoading"
      :lines="15"
      :hasContentImg="false"
      contentIsFullWidth
    />
    <div id="sumsub-websdk" v-show="!isLoading"></div>
    <template v-if="showActionModal">
      <BaseModal>
        <ActionModal
          :modalOptions="modalOptions"
          @onButtonClick="toggleActionModal()"
        />
      </BaseModal>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";

import { useIdentityQueueStore } from "@/stores/identityQueueStore";

import useScriptTagSetup from "@/composables/helpers/sciptTagSetup.js";
import useApiHelpers from "@/composables/api/helpers";

import BaseModal from "@/components/ui/modals/BaseModal.vue";
import ActionModal from "@/components/ui/modals/ActionModal.vue";
import SkeleLoader from "@/components/ui/loaders/SkeleLoader.vue";

const identityQueueStore = useIdentityQueueStore();
const { postRequest } = useApiHelpers();

const router = useRouter();
const { setupScriptTag } = useScriptTagSetup();
const SUMSUB_JS_SDK_URL =
  "https://static.sumsub.com/idensic/static/sns-websdk-builder.js";

const snsWebSdkInstance = ref(null);
let token = ref("");
let sumsubApiUrl = ref("");
const showActionModal = ref(false);
const isLoading = ref(true);

const modalOptions = ref({ title: "", text: "" });

async function getTokenResponse(response) {
  token = response.data.token;
  sumsubApiUrl = response.data.apiUrl;
}

async function getSumsubToken() {
  await postRequest("/sumsub/kyc/token", {}, getTokenResponse);
}

const localOnlyWorkaround = () => {
  identityQueueStore.removeSumsubItems();
  router.push(identityQueueStore.getNextIdentityRoute);
};

const toggleActionModal = () => {
  showActionModal.value = !showActionModal.value;
};
const showModalOnError = (errorMsg) => {
  modalOptions.value = {
    title: "Oops there has been an issue with your document",
    text: `${errorMsg}`,
  };
  toggleActionModal();
};

function launchWebSdk(accessToken) {
  snsWebSdkInstance.value = snsWebSdk
    .init(accessToken, () => accessToken)
    .withBaseUrl(sumsubApiUrl)
    .withConf({
      lang: "en",
      onMessage: (type, payload) => {
        console.log("WebSDK onMessage", type, payload);
      },
      uiConf: {
        customCssStr:
          ":root {\n  --black: #000000;\n   --grey: #F5F5F5;\n  --grey-darker: #B2B2B2;\n  --border-color: #DBDBDB;\n}\n\np {\n  color: var(--black);\n  font-size: 16px;\n  line-height: 24px;\n}\n\nsection {\n  margin: 40px auto;\n}\n\ninput {\n  color: var(--black);\n  font-weight: 600;\n  outline: none;\n}\n\nsection.content {\n  background-color: var(--grey);\n  color: var(--black);\n  padding: 40px 40px 16px;\n  box-shadow: none;\n  border-radius: 6px;\n}\n\nbutton.submit,\nbutton.back {\n  text-transform: capitalize;\n  border-radius: 6px;\n  height: 48px;\n  padding: 0 30px;\n  font-size: 16px;\n  background-image: none !important;\n  transform: none !important;\n  box-shadow: none !important;\n  transition: all 0.2s linear;\n}\n\nbutton.submit {\n  min-width: 132px;\n  background: none;\n  background-color: var(--black);\n}\n\n.round-icon {\n  background-color: var(--black) !important;\n  background-image: none !important;\n}",
      },
      onError: (error) => {
        console.error("WebSDK onError", error);
        showModalOnError(error);
      },
    })
    .withOptions({ addViewportTag: false, adaptIframeHeight: true })
    .on("idCheck.applicantStatus", (payload) => {
      postRequest("/documents/sumsub", payload, localOnlyWorkaround);
    })
    .on("idCheck.onInitialized", (payload) => {
      isLoading.value = false;
    })
    .on("idCheck.onError", (error) => {
      showModalOnError(error.error);
    })
    .on("onError", (error, payload) => {
      const { msg } = payload;
      console.error(error, payload);
      showModalOnError(msg);
    })
    .onMessage((type, payload) => {
      const { msg } = payload;
      if (msg && type === "idCheck.onUploadError") showModalOnError(msg);
    })
    .build();
  snsWebSdkInstance.value.launch("#sumsub-websdk");
}

onMounted(async () => {
  await getSumsubToken();
  setupScriptTag(SUMSUB_JS_SDK_URL).then(() => launchWebSdk(token));
});
</script>
<style lang="scss" scoped>
.form {
  .bodycopy {
    margin-bottom: 1rem;
    @include breakpoint($min-m) {
      margin-bottom: 1.5rem;
    }
  }
  &__selfie-instructions {
    list-style: unset;
    padding-inline: 1.5rem;

    li {
      font-size: $font-body;
      line-height: 1.5;
    }
  }
}
</style>
